import { harvestMockIds } from './idHelper';

// Centralized loader for screen-specific training data.
// Training data drives tour "state simulation": each step can carry a mockState
// payload that components apply when the tour is active, so users see what the
// UI looks like in different stages of a record's lifecycle.
//
// To register training data for a new screen, import its module and add it to
// trainingDataMap below keyed by the screenId from common.screens.

class TrainingDataLoader {
    constructor() {
        // Modules that have actually been loaded and self-registered.
        // Shape: { [screenId]: { getStepState(stepNumber) => mockState | null } }
        this.trainingDataMap = {};
        // Per-screen dynamic-import factories. Registered at app boot (cheap),
        // but the modules themselves only load when ensureLoaded runs.
        // Shape: { [screenId]: () => Promise<unknown> }
        this.importers = {};
        // De-dupe concurrent ensureLoaded calls for the same screen so a
        // double-mount or rapid tour-open doesn't fire two network fetches.
        this.pendingLoads = new Map();
    }

    /**
     * Register a dynamic-import factory for a screen. Call at app boot
     * (see sopui/src/training/index.js). The factory is invoked once,
     * lazily, the first time ensureLoaded runs for this screen.
     */
    registerImporter(screenId, importer) {
        if (!screenId || typeof importer !== 'function') return;
        this.importers[screenId] = importer;
    }

    register(screenId, trainingModule) {
        if (!screenId || !trainingModule) return;
        this.trainingDataMap[screenId] = trainingModule;
    }

    /**
     * Resolve the training module for a screen, dynamically importing it the
     * first time. Returns the module (or null if no importer is registered).
     * Subsequent calls return immediately from the loaded map.
     */
    async ensureLoaded(screenId) {
        if (!screenId) return null;
        if (this.trainingDataMap[screenId]) return this.trainingDataMap[screenId];
        const importer = this.importers[screenId];
        if (!importer) return null;
        if (!this.pendingLoads.has(screenId)) {
            const promise = importer()
                .then(() => {
                    this.pendingLoads.delete(screenId);
                    return this.trainingDataMap[screenId] || null;
                })
                .catch(err => {
                    this.pendingLoads.delete(screenId);
                    console.error(`Failed to load training module for screen ${screenId}:`, err);
                    return null;
                });
            this.pendingLoads.set(screenId, promise);
        }
        return this.pendingLoads.get(screenId);
    }

    loadTrainingData(screenId, stepNumber = null) {
        const trainingModule = this.trainingDataMap[screenId];
        if (!trainingModule) return null;

        if (stepNumber !== null) {
            const stepData = trainingModule.getStepState?.(stepNumber);
            if (!stepData) return null;
            // Register this step's fake ids before the payload reaches any
            // component. guideHelper.applyTrainingForStep calls this ahead of
            // the SET_RECORD dispatch, so no widget can observe a mock id
            // before tourInterceptor knows to block requests carrying it.
            harvestMockIds(stepData);
            return {
                ...stepData,
                _trainingMode: true,
                _stepNumber: stepNumber,
                _screenId: screenId
            };
        }

        return trainingModule;
    }

    hasTrainingData(screenId) {
        // True if loaded OR there's a registered importer waiting to lazy-load.
        return Object.prototype.hasOwnProperty.call(this.trainingDataMap, screenId)
            || Object.prototype.hasOwnProperty.call(this.importers, screenId);
    }

    getAvailableTrainingScreens() {
        // Union of loaded + lazy-importable screens.
        return Array.from(new Set([
            ...Object.keys(this.trainingDataMap),
            ...Object.keys(this.importers)
        ]));
    }

    loadTrainingDataForGuide(guide, screenId, stepNumber = null) {
        if (!this.shouldUseTrainingData(guide, screenId)) return null;
        return this.loadTrainingData(screenId, stepNumber);
    }

    shouldUseTrainingData(guide, screenId) {
        // Training mode is gated purely on whether a training module is
        // registered for this screen — the registration itself is the explicit
        // signal that the screen's tour drives a state simulation. Earlier
        // versions also string-matched the guide name against markers like
        // 'training'/'demo', but the server returns the i18n-resolved name
        // (e.g. 'Cultivation batch tour' for guide.cultivationBatchTraining.title),
        // so the marker word disappears and the gate falsely flipped to off.
        return !!guide && this.hasTrainingData(screenId);
    }

    isTrainingGuide(guide) {
        // Retained for backward compat with any external callers; same signal
        // as shouldUseTrainingData minus the screenId dimension. New code
        // should prefer shouldUseTrainingData(guide, screenId).
        if (!guide) return false;
        const tags = guide.tags || [];
        return tags.includes('training') || tags.includes('demo')
            || tags.includes('tutorial') || tags.includes('walkthrough');
    }

    loadMultipleSteps(screenId, stepNumbers) {
        const result = {};
        stepNumbers.forEach(stepNumber => {
            const stepData = this.loadTrainingData(screenId, stepNumber);
            if (stepData) result[stepNumber] = stepData;
        });
        return result;
    }

    getBaseState(screenId) {
        const trainingModule = this.trainingDataMap[screenId];
        if (!trainingModule) return null;
        if (typeof trainingModule.getStepState === 'function') {
            return trainingModule.getStepState(0) || trainingModule.getStepState(1);
        }
        return trainingModule;
    }
}

const trainingDataLoader = new TrainingDataLoader();
export default trainingDataLoader;
